import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers, faBuilding, faBriefcase, faGraduationCap,
  faCoins, faCogs, faUserShield, faUserTie, faChalkboardTeacher,
  faUserClock, faCalculator, faEye, faFileContract,
  faCalendarCheck, faChartPie, faHandshake, faUserPlus,
  faFileInvoice, faCreditCard, faArchive, faClipboardList,
  faDatabase, faCog, faRocket, faServer, faShieldAlt,
  faCheckDouble, faClock, faMapMarkedAlt, faUserCheck,
  faCheckCircle, faExchangeAlt, faUniversity, faUser,
  faWallet, faCalendarAlt, faBook, faFileSignature,
  faGlobe, faDownload, faSpinner
} from '@fortawesome/free-solid-svg-icons';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const PresentationBisenter = () => {
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const contentRef = useRef(null);

  const generatePDF = async () => {
    if (pdfGenerating) return;
    setPdfGenerating(true);

    try {
      const element = contentRef.current;
      if (!element) return;

      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: '#0f172a',
        logging: false,
        allowTaint: true,
        useCORS: true,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save('presentation_bisenter_erp.pdf');
    } catch (error) {
      console.error('Erreur PDF :', error);
    } finally {
      setPdfGenerating(false);
    }
  };

  return (
    <>
      <style>{`
        /* ============================================================
           STYLES – PRESENTATION BISENTER ERP AMÉLIORÉE
           ============================================================ */
        :root {
          --bg-gradient: radial-gradient(circle at 10% 20%, #0f172a, #020617);
          --card-bg: rgba(255,255,255,0.04);
          --card-border: rgba(255,255,255,0.06);
          --text-primary: #e2e8f0;
          --text-secondary: #94a3b8;
          --accent-blue: #3b82f6;
          --accent-cyan: #06b6d4;
          --accent-green: #10b981;
          --accent-amber: #f59e0b;
          --accent-rose: #f43f5e;
          --radius: 20px;
          --shadow: 0 20px 60px rgba(0,0,0,0.6);
        }

        .presentation-container {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: 2rem 1.5rem;
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          line-height: 1.6;
          animation: fadeIn 0.8s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .presentation-container h1,
        .presentation-container h2,
        .presentation-container h3 {
          font-weight: 600;
          letter-spacing: -0.02em;
        }

        .presentation-container h1 {
          font-size: clamp(2.2rem, 5vw, 3.6rem);
          background: linear-gradient(135deg, #60a5fa, #06b6d4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 0.5rem;
        }

        .presentation-container .subtitle {
          font-size: clamp(1rem, 1.4vw, 1.3rem);
          color: var(--text-secondary);
          margin-bottom: 2rem;
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: 1.8rem;
          box-shadow: var(--shadow);
          transition: all 0.3s ease;
          margin-bottom: 2rem;
        }
        .glass-card:hover {
          border-color: rgba(59,130,246,0.2);
          transform: translateY(-4px);
        }

        .badge-role {
          display: inline-block;
          padding: 0.3rem 1rem;
          border-radius: 40px;
          font-size: 0.8rem;
          font-weight: 500;
          background: rgba(59,130,246,0.12);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.1);
          margin: 0.2rem;
          transition: 0.2s;
        }
        .badge-role:hover {
          transform: scale(1.05);
        }
        .badge-role.admin { background: rgba(244,63,94,0.15); color: #f43f5e; border-color: rgba(244,63,94,0.15); }
        .badge-role.dsi { background: rgba(6,182,212,0.15); color: #06b6d4; border-color: rgba(6,182,212,0.15); }
        .badge-role.trainer { background: rgba(251,146,60,0.15); color: #fb923c; border-color: rgba(251,146,60,0.15); }
        .badge-role.hr { background: rgba(16,185,129,0.15); color: #10b981; border-color: rgba(16,185,129,0.15); }
        .badge-role.accountant { background: rgba(245,158,11,0.15); color: #f59e0b; border-color: rgba(245,158,11,0.15); }
        .badge-role.supervisor { background: rgba(139,92,246,0.15); color: #a78bfa; border-color: rgba(139,92,246,0.15); }

        .module-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1.2rem;
          margin-top: 0.5rem;
        }
        .module-item {
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.04);
          border-radius: 14px;
          padding: 1rem 1.2rem;
          transition: 0.3s;
          display: flex;
          align-items: flex-start;
          gap: 0.8rem;
          cursor: default;
        }
        .module-item:hover {
          background: rgba(255,255,255,0.06);
          border-color: rgba(59,130,246,0.2);
          transform: translateX(4px);
        }
        .module-item .icon {
          font-size: 1.3rem;
          min-width: 28px;
          color: var(--accent-blue);
          margin-top: 0.1rem;
        }
        .module-item .content {
          flex: 1;
        }
        .module-item .content .title {
          font-weight: 500;
          color: #fff;
        }
        .module-item .content .desc {
          font-size: 0.85rem;
          color: var(--text-secondary);
        }

        .section-title {
          font-size: clamp(1.5rem, 2.5vw, 2rem);
          font-weight: 600;
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          gap: 0.8rem;
          position: relative;
        }
        .section-title .icon-title {
          color: var(--accent-blue);
        }
        .section-title::after {
          content: '';
          flex: 1;
          height: 2px;
          background: linear-gradient(to right, rgba(59,130,246,0.2), transparent);
          margin-left: 1rem;
        }

        .user-roles {
          display: flex;
          flex-wrap: wrap;
          gap: 0.6rem;
          margin-top: 0.5rem;
        }

        .footer-note {
          text-align: center;
          padding-top: 2rem;
          border-top: 1px solid rgba(255,255,255,0.04);
          color: var(--text-secondary);
          font-size: 0.85rem;
        }
        .footer-note strong {
          color: var(--text-primary);
        }

        .pdf-btn {
          background: rgba(239,68,68,0.15);
          border: 1px solid rgba(239,68,68,0.3);
          color: #f87171;
          padding: 0.5rem 1.2rem;
          border-radius: 40px;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 500;
          cursor: pointer;
          transition: 0.3s;
          font-size: 0.9rem;
        }
        .pdf-btn:hover:not(:disabled) {
          background: rgba(239,68,68,0.25);
          transform: scale(1.02);
        }
        .pdf-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .header-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }

        @media (max-width: 768px) {
          .presentation-container { padding: 1.2rem; }
          .glass-card { padding: 1.2rem; }
          .module-grid { grid-template-columns: 1fr; }
          .header-actions { flex-direction: column; align-items: stretch; }
          .pdf-btn { justify-content: center; }
        }
      `}</style>

      <div className="presentation-container" ref={contentRef}>
        {/* ===== EN-TÊTE ===== */}
        <div className="glass-card" style={{ textAlign: 'center', paddingBottom: '2.5rem' }}>
          <div className="header-actions">
            <div style={{ flex: 1 }}>
              <h1>🚀 Bisenter ERP</h1>
              <div className="subtitle">
                La solution de gestion d'entreprise tout-en-un pour piloter vos ressources humaines, vos partenariats, vos activités, vos formations et votre comptabilité. Conçu pour les entreprises modernes, Bisenter ERP centralise vos données, automatise vos processus et vous offre une vision 360° de votre activité.
              </div>
            </div>
            <button className="pdf-btn" onClick={generatePDF} disabled={pdfGenerating}>
              {pdfGenerating ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Génération...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faDownload} /> Télécharger PDF
                </>
              )}
            </button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', flexWrap: 'wrap', marginTop: '1rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}><FontAwesomeIcon icon={faRocket} color="#3b82f6" /> ERP Moderne</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}><FontAwesomeIcon icon={faServer} color="#06b6d4" /> Cloud Ready</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}><FontAwesomeIcon icon={faShieldAlt} color="#10b981" /> Sécurisé</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}><FontAwesomeIcon icon={faCheckDouble} color="#f59e0b" /> 6 Modules</span>
          </div>
        </div>

        {/* ===== UTILISATEURS ===== */}
        <div className="glass-card">
          <h2 className="section-title">
            <FontAwesomeIcon icon={faUsers} className="icon-title" />
            Profils utilisateurs
          </h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '0.8rem' }}>
            L’ERP Bisenter s’adapte à 6 rôles distincts, chacun avec des permissions et des vues personnalisées, garantissant une sécurité optimale et une expérience adaptée aux besoins de chaque métier.
          </p>
          <div className="user-roles">
            <span className="badge-role admin"><FontAwesomeIcon icon={faUserShield} /> Super administrateur – Gestion globale du système, paramétrage et sécurité</span>
            <span className="badge-role dsi"><FontAwesomeIcon icon={faUserTie} /> DSI – Direction du système d'information, maintenance et infrastructure</span>
            <span className="badge-role trainer"><FontAwesomeIcon icon={faChalkboardTeacher} /> Formateur – Animation des formations, suivi des apprenants</span>
            <span className="badge-role hr"><FontAwesomeIcon icon={faUserClock} /> RH – Ressources humaines : paie, recrutement, gestion des talents</span>
            <span className="badge-role accountant"><FontAwesomeIcon icon={faCalculator} /> Comptable – Gestion financière, facturation, trésorerie</span>
            <span className="badge-role supervisor"><FontAwesomeIcon icon={faEye} /> Superviseur – Contrôle des opérations, reporting et audit</span>
          </div>
        </div>

        {/* ===== MODULE 1 – RESSOURCES HUMAINES ===== */}
        <div className="glass-card">
          <h2 className="section-title">
            <FontAwesomeIcon icon={faUsers} className="icon-title" style={{ color: 'var(--accent-blue)' }} />
            1. RESSOURCES HUMAINES
          </h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
            Le module RH centralise l'intégralité de la gestion du personnel, du recrutement à la paie, en passant par la formation, les évaluations et les missions. Il permet de suivre chaque collaborateur tout au long de son parcours dans l'entreprise.
          </p>
          <div className="module-grid">
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faUserPlus} /></div>
              <div className="content">
                <div className="title">1.1 Gestion des employés</div>
                <div className="desc">Création et suivi des fiches individuelles des employés (identité, poste, compétences, historique). Permet également la modification et la désactivation des profils.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faCalendarCheck} /></div>
              <div className="content">
                <div className="title">1.2 Gestion des congés</div>
                <div className="desc">Système complet de demandes, validations, suivi des soldes et historique des absences (congés payés, maladie, sans solde, etc.).</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faClock} /></div>
              <div className="content">
                <div className="title">1.3 Présences / Absences / Retards</div>
                <div className="desc">Pointage quotidien, suivi des heures travaillées, génération de rapports d'assiduité pour faciliter la paie et l'évaluation.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faClipboardList} /></div>
              <div className="content">
                <div className="title">1.4 Évaluation des employés</div>
                <div className="desc">Grilles d'évaluation personnalisées, entretiens annuels, suivi des performances et des compétences pour favoriser le développement professionnel.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faMapMarkedAlt} /></div>
              <div className="content">
                <div className="title">1.5 Missions & rapports</div>
                <div className="desc">Planification et suivi des missions de supervision, avec génération automatique de rapports d'activité pour les clients internes et externes.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faUserCheck} /></div>
              <div className="content">
                <div className="title">1.6 Profil individuel</div>
                <div className="desc">Espace personnel pour chaque employé avec CV, compétences, formations suivies, évaluations et historique des missions.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faFileContract} /></div>
              <div className="content">
                <div className="title">1.7 Contrats de travail</div>
                <div className="desc">Génération et suivi des contrats de travail (CDI, CDD, intérim) avec gestion des signatures et des avenants.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faFileInvoice} /></div>
              <div className="content">
                <div className="title">1.8 Attestation de services</div>
                <div className="desc">Édition automatisée d'attestations de travail, de stage ou de services, conformes aux normes légales.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faCreditCard} /></div>
              <div className="content">
                <div className="title">1.9 Carte de service</div>
                <div className="desc">Génération de cartes professionnelles personnalisées pour les employés, avec QR code et informations de contact.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faChartPie} /></div>
              <div className="content">
                <div className="title">1.10 Tableau de bord RH</div>
                <div className="desc">Indicateurs clés de gestion RH : effectifs, turnover, absentéisme, pyramide des âges, coûts salariaux, etc.</div>
              </div>
            </div>
          </div>
        </div>

        {/* ===== MODULE 2 – PARTENAIRES ===== */}
        <div className="glass-card">
          <h2 className="section-title">
            <FontAwesomeIcon icon={faHandshake} className="icon-title" style={{ color: 'var(--accent-cyan)' }} />
            2. PARTENAIRES
          </h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
            Ce module centralise toutes les interactions avec vos partenaires extérieurs : fournisseurs, entreprises clientes, offre d'emploi et placements. Il facilite la gestion des relations B2B et le suivi des contrats.
          </p>
          <div className="module-grid">
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faBuilding} /></div>
              <div className="content">
                <div className="title">2.1 Fournisseurs de services</div>
                <div className="desc">Enregistrement et suivi des prestataires, des contrats et des évaluations de performance.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faBuilding} /></div>
              <div className="content">
                <div className="title">2.2 Entreprises</div>
                <div className="desc">Fiche complète de chaque entreprise partenaire (coordonnées, secteur, historique des placements et des échanges).</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faUserTie} /></div>
              <div className="content">
                <div className="title">2.3 Profil d’entreprise</div>
                <div className="desc">Espace dédié pour chaque entreprise avec toutes les informations légales, les offres, les placements et les factures.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faBriefcase} /></div>
              <div className="content">
                <div className="title">2.4 Offres d’emploi</div>
                <div className="desc">Publication et gestion des offres d'emploi pour le compte des entreprises, avec suivi des candidatures.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faUserCheck} /></div>
              <div className="content">
                <div className="title">2.5 Placement des candidats</div>
                <div className="desc">Mise en relation des candidats acceptés avec les offres d'emploi des entreprises partenaires.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faFileInvoice} /></div>
              <div className="content">
                <div className="title">2.6 Facturation entreprise</div>
                <div className="desc">Émission, envoi et suivi des factures à destination des entreprises clientes.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faChartPie} /></div>
              <div className="content">
                <div className="title">2.7 Tableau de bord partenaires</div>
                <div className="desc">Indicateurs de performance : nombre de placements, chiffre d'affaires généré, taux de satisfaction client.</div>
              </div>
            </div>
          </div>
        </div>

        {/* ===== MODULE 3 – ACTIVITÉS ===== */}
        <div className="glass-card">
          <h2 className="section-title">
            <FontAwesomeIcon icon={faBriefcase} className="icon-title" style={{ color: 'var(--accent-amber)' }} />
            3. ACTIVITÉS
          </h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
            Le module Activités gère l'intégralité du processus de recrutement, de la publication d'offres à l'intégration des nouveaux collaborateurs, en passant par la gestion des candidatures et des entretiens.
          </p>
          <div className="module-grid">
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faBriefcase} /></div>
              <div className="content">
                <div className="title">3.1 Offres d’emploi</div>
                <div className="desc">Création, modification et suppression des offres d'emploi avec tous les détails nécessaires (mission, compétences, conditions).</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faGlobe} /></div>
              <div className="content">
                <div className="title">3.2 Affichage public</div>
                <div className="desc">Les offres sont automatiquement publiées sur le site internet de Bisenter pour attirer les candidats.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faFileSignature} /></div>
              <div className="content">
                <div className="title">3.3 Formulaire de postulation</div>
                <div className="desc">Interface de candidature en ligne simple et intuitive, permettant le dépôt de CV et de lettre de motivation.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faUsers} /></div>
              <div className="content">
                <div className="title">3.4 Gestion des candidatures</div>
                <div className="desc">Suivi des candidats à chaque étape (reçue, présélectionnée, entretien, acceptée, refusée) avec gestion des communications.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faUser} /></div>
              <div className="content">
                <div className="title">3.5 Profil candidat & CV</div>
                <div className="desc">Consultation détaillée des CV, évaluation des compétences et historique des échanges avec chaque candidat.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faHandshake} /></div>
              <div className="content">
                <div className="title">3.6 Placement & création employé</div>
                <div className="desc">Intégration des candidats acceptés dans une entreprise partenaire et création automatique de leur profil employé dans le système.</div>
              </div>
            </div>
          </div>
        </div>

        {/* ===== MODULE 4 – FORMATIONS ===== */}
        <div className="glass-card">
          <h2 className="section-title">
            <FontAwesomeIcon icon={faGraduationCap} className="icon-title" style={{ color: 'var(--accent-green)' }} />
            4. FORMATIONS
          </h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
            Ce module permet de créer, organiser et suivre l'ensemble de vos programmes de formation, de la conception du catalogue jusqu'à la certification des apprenants.
          </p>
          <div className="module-grid">
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faBook} /></div>
              <div className="content">
                <div className="title">4.1 Enregistrement des formations</div>
                <div className="desc">Catalogue complet des formations avec objectifs, prérequis, contenu pédagogique et tarifs.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faCalendarAlt} /></div>
              <div className="content">
                <div className="title">4.2 Sessions de formation</div>
                <div className="desc">Planification des sessions : dates, lieux, capacité, inscription des participants.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faChalkboardTeacher} /></div>
              <div className="content">
                <div className="title">4.3 Assignation des formateurs</div>
                <div className="desc">Affectation des formateurs aux sessions, suivi de leur charge de travail et évaluation de leur performance.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faChartPie} /></div>
              <div className="content">
                <div className="title">4.4 Tableau de bord Formation</div>
                <div className="desc">Statistiques détaillées : taux de participation, satisfaction des apprenants, certifications obtenues.</div>
              </div>
            </div>
          </div>
        </div>

        {/* ===== MODULE 5 – COMPTABILITÉ & CAISSE ===== */}
        <div className="glass-card">
          <h2 className="section-title">
            <FontAwesomeIcon icon={faCoins} className="icon-title" style={{ color: 'var(--accent-rose)' }} />
            5. COMPTABILITÉ & CAISSE
          </h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
            Le module Comptabilité & Caisse offre une vision complète et en temps réel de la santé financière de l'entreprise, avec une gestion rigoureuse des flux de trésorerie, de la facturation et des paiements.
          </p>
          <div className="module-grid">
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faExchangeAlt} /></div>
              <div className="content">
                <div className="title">5.1 Mouvements de caisse</div>
                <div className="desc">Enregistrement des encaissements et décaissements avec historique complet et traçabilité.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faWallet} /></div>
              <div className="content">
                <div className="title">5.2 Gestion des dépenses</div>
                <div className="desc">Suivi des charges par catégorie, avec justificatifs et validation en plusieurs étapes.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faGraduationCap} /></div>
              <div className="content">
                <div className="title">5.3 Vente de formations</div>
                <div className="desc">Facturation des sessions de formation, encaissement et suivi des règlements.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faUser} /></div>
              <div className="content">
                <div className="title">5.4 Gestion des clients</div>
                <div className="desc">Fiche client complète avec historique des achats, relances et suivi des paiements.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faFileInvoice} /></div>
              <div className="content">
                <div className="title">5.5 Profil client – Factures</div>
                <div className="desc">Consultation de toutes les factures émises et suivi des règlements en temps réel.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faUniversity} /></div>
              <div className="content">
                <div className="title">5.6 Comptes bancaires</div>
                <div className="desc">Enregistrement et gestion des comptes bancaires de l'entreprise (édition, modification, suppression).</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faExchangeAlt} /></div>
              <div className="content">
                <div className="title">5.7 Virements Caisse ↔ Banque</div>
                <div className="desc">Transferts sécurisés entre la caisse et les comptes bancaires avec traçabilité complète.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faCheckCircle} /></div>
              <div className="content">
                <div className="title">5.8 Validation facture entreprise</div>
                <div className="desc">Processus de validation des factures en plusieurs étapes avec workflow d'approbation.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faUserClock} /></div>
              <div className="content">
                <div className="title">5.9 Paiement et avance sur salaire</div>
                <div className="desc">Gestion des bulletins de paie, des avances et des acomptes pour les employés.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faFileContract} /></div>
              <div className="content">
                <div className="title">5.10 Fiches de paie</div>
                <div className="desc">Génération automatisée des fiches de paie au format PDF, avec tous les détails des rémunérations et cotisations.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faCog} /></div>
              <div className="content">
                <div className="title">5.11 Paramètres de paiement</div>
                <div className="desc">Configuration des modes de paiement, des devises, des taux de change et des seuils d'approbation.</div>
              </div>
            </div>
          </div>
        </div>

        {/* ===== MODULE 6 – ADMINISTRATION ===== */}
        <div className="glass-card">
          <h2 className="section-title">
            <FontAwesomeIcon icon={faCogs} className="icon-title" style={{ color: 'var(--accent-amber)' }} />
            6. ADMINISTRATION
          </h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
            Le module Administration centralise tous les paramétrages avancés, la gestion des utilisateurs et des droits, ainsi que les opérations de maintenance et de sécurisation des données.
          </p>
          <div className="module-grid">
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faArchive} /></div>
              <div className="content">
                <div className="title">6.1 Archivage numérique</div>
                <div className="desc">Stockage sécurisé de tous les documents importants (contrats, factures, rapports) avec historique des versions.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faClipboardList} /></div>
              <div className="content">
                <div className="title">6.2 Critères d’évaluation</div>
                <div className="desc">Définition et personnalisation des grilles d'évaluation pour les employés, les formations et les partenaires.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faBuilding} /></div>
              <div className="content">
                <div className="title">6.3 Informations de l’entreprise</div>
                <div className="desc">Gestion des coordonnées, du logo, des informations légales et des paramètres généraux de l'entreprise.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faUserShield} /></div>
              <div className="content">
                <div className="title">6.4 Gestion des administrateurs</div>
                <div className="desc">Création, modification et attribution des rôles et permissions pour chaque administrateur du système.</div>
              </div>
            </div>
            <div className="module-item">
              <div className="icon"><FontAwesomeIcon icon={faDatabase} /></div>
              <div className="content">
                <div className="title">6.5 Réinitialisation des tables</div>
                <div className="desc">Opérations de maintenance avancées : vidage ou restauration des tables de données en cas de besoin.</div>
              </div>
            </div>
          </div>
        </div>

        {/* ===== PIED DE PAGE ===== */}
        <div className="footer-note">
          <p>
            <strong>Bisenter ERP</strong> – La solution intégrée pour piloter votre entreprise avec agilité et efficacité. <br />
            Tous les modules sont interconnectés pour une vision 360° de votre activité, vous permettant de prendre des décisions éclairées en temps réel.
          </p>
          <p style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            © {new Date().getFullYear()} Bisenter – Tous droits réservés.
          </p>
        </div>
      </div>
    </>
  );
};

export default PresentationBisenter;